import { css } from 'emotion';
import { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

import { useContextTypes } from '../../hooks/use-context-types';
import { DisplayGridColumnPropType } from '../../prop-types/display-grid-column';
import { DisplayGridRecordPropType } from '../../prop-types/display-grid-record';
import { DisplayGridGroupRecordPropType } from '../../prop-types/display-grid-group-record';
import { GridHelperService } from '../../services/grid-helper-service';

import { sanitize } from './display-grid-body/index';
import { Context } from './context';
import { RowContext } from './row-context';
import displayGridBodyStyle from './display-grid-body/index.module.less';
import displayGridColumnGroupStyle from './display-grid-column-group.module.less';
import displayGridHeaderStyle from './display-grid-header/index.module.less';

/*
 * A bunch of math and style construction via 'emotion' that doesn't need
 * to clutter up the main component file, and makes a good candidate for
 * memoization, so we can just isolate it into its own hook function here.
 */
export const useGridClassNameGenerator = () => {
  useContextTypes('useGridClassNameGenerator', Context, {
    columns: PropTypes.arrayOf(DisplayGridColumnPropType).isRequired,
    data: PropTypes.arrayOf(PropTypes.oneOfType([DisplayGridRecordPropType, DisplayGridGroupRecordPropType])).isRequired,
    getColumnId: PropTypes.func.isRequired,
    leftoverColumnWidth: PropTypes.number,
    nonPinnedColumns: PropTypes.arrayOf(DisplayGridColumnPropType).isRequired,
    pinnedColumns: PropTypes.arrayOf(DisplayGridColumnPropType).isRequired,
    pinnedColumnsWidth: PropTypes.number.isRequired,
    totalWidth: PropTypes.number.isRequired,
    width: PropTypes.number,
  });

  const {
    columns,
    data,
    getColumnId,
    leftoverColumnWidth,
    nonPinnedColumns,
    pinnedColumns,
    pinnedColumnsWidth,
    totalWidth,
    width,
    activeGroupByColumns,
  } = useContext(Context);

  const { expanded } = useContext(RowContext);

  return useMemo(() => {
    const rowHeight = GridHelperService.getRowHeight();
    const nonPinnedColumnsWidth = totalWidth - pinnedColumnsWidth;
    const expandedHeight = data.reduce((totalHeight, datum) => {
      const hasGroupByRows = !!activeGroupByColumns.length;
      const primaryRow = GridHelperService.getPrimaryRow(datum) || {};
      const numGridRows = GridHelperService.getGridRowCount(datum, expanded.has(primaryRow.id), hasGroupByRows);

      return totalHeight + numGridRows * rowHeight;
    }, rowHeight /* header row height */);

    const nonPinnedGridTemplateColumns = nonPinnedColumns.reduce((gridTemplateColumns, column, index) => {
      let columnsTemplate = `${gridTemplateColumns}${sanitize(getColumnId(column))}-start] ${column.width || column.minwidth}px [${sanitize(getColumnId(column))}-end `;

      // If our columns don't fill up the available container area, add an artificial padding column for the rest
      if (index === nonPinnedColumns.length - 1 && leftoverColumnWidth) {
        columnsTemplate += `leftovers-start] ${leftoverColumnWidth}px [leftovers-end`;
      }

      return columnsTemplate;
    }, '');

    const pinnedColumnsGridTemplateColumns = pinnedColumns.reduce((gridTemplateColumns, column) => {
      return `${gridTemplateColumns}${sanitize(getColumnId(column))}-start] ${column.width || column.minWidth}px [${sanitize(getColumnId(column))}-end `;
    }, '');

    return css`
      height: ${expandedHeight}px;
      min-height: 100%;
      min-width: 100%;
      width: ${width > totalWidth ? width : totalWidth}px;

      .${displayGridBodyStyle['grid-rows']} {
        [data-column='checkbox'] {
          grid-area: checkbox;
        }
        ${pinnedColumns.map(
          (column) => `
          [data-column="${getColumnId(column)}"] {
            grid-area: ${sanitize(getColumnId(column))};
          }
          `,
        )}
        ${nonPinnedColumns.map(
          (column) => `
          [data-column="${getColumnId(column)}"] {
            grid-area: ${sanitize(getColumnId(column))};
          }
          `,
        )}
          [data-column] {
          grid-row: auto;
        }
      }

      .${displayGridHeaderStyle['header-row']} {
        [data-column='checkbox'] {
          width: ${GridHelperService.getCheckboxColumnWidth()}px;
        }

        .draggable-header-cell {
          display: inline-block;
          position: relative;

          [data-column] {
            cursor: inherit;
            flex-basis: 100%;
            max-width: 100%;
          }
        }
      }

      .${displayGridColumnGroupStyle['non-pinned-columns']} {
        max-width: calc(100% - ${pinnedColumnsWidth});
        min-width: ${nonPinnedColumnsWidth}px;

        .${displayGridBodyStyle['grid-rows']} {
          grid-template-columns: [ ${nonPinnedGridTemplateColumns}];
        }

        .${displayGridBodyStyle.expandable} {
          &.${displayGridBodyStyle.expanded} > * {
            display: block;

            &:nth-last-of-type(-n + ${nonPinnedColumns.length}) {
              border-bottom: 1px solid #eee;
            }
          }
        }
      }

      .${displayGridColumnGroupStyle['pinned-columns']} {
        .${displayGridBodyStyle['grid-rows']} {
          grid-template-columns: [checkbox-start] ${GridHelperService.getCheckboxColumnWidth()}px [checkbox-end ${pinnedColumnsGridTemplateColumns}];
        }

        .${displayGridBodyStyle.expandable} {
          &.${displayGridBodyStyle.expanded} > * {
            display: block;

            &:nth-last-of-type(-n + ${pinnedColumns.length + 1}) {
              border-bottom: 1px solid #eee;
            }
          }
        }

        [data-column]:last-child {
          border-right: 0;
        }
      }
    `;
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, data, expanded, getColumnId, leftoverColumnWidth, width, activeGroupByColumns]);
};
